import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { keyframes } from '@mui/system';
import AnimatedButton from '../components/AnimatedButton';
import SwipeableCardStack from '../components/SwipeableCardStack';

const backgroundAnimation = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const Demo = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [location, setLocation] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [weatherData, setWeatherData] = useState(null);
  const [weatherError, setWeatherError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionToken, setSessionToken] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const timeoutRef = useRef(null);
  const searchBoxRef = useRef(null);

  // Date picker state
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);

  // Budget and group size state
  const [budget, setBudget] = useState('');
  const [groupSize, setGroupSize] = useState('');

  // Trip type and preferences state
  const [selectedTripTypes, setSelectedTripTypes] = useState([]);
  const [personalPreferences, setPersonalPreferences] = useState('');
  const [showTripTypeDropdown, setShowTripTypeDropdown] = useState(false);

  // Wake up time state
  const [wakeUpTime, setWakeUpTime] = useState('');

  // API response state
  const [eventsData, setEventsData] = useState(null);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const [apiError, setApiError] = useState(null);

  const tripTypes = [
    { id: 'family', label: 'Family', icon: '👨‍👩‍👧‍👦' },
    { id: 'solo', label: 'Solo', icon: '🧳' },
    { id: 'friends', label: 'Friends', icon: '👥' },
    { id: 'couple', label: 'Couple', icon: '💑' },
    { id: 'business', label: 'Business', icon: '💼' },
    { id: 'adventure', label: 'Adventure', icon: '🏔️' },
    { id: 'relaxation', label: 'Relaxation', icon: '🏖️' },
    { id: 'cultural', label: 'Cultural', icon: '🏛️' }
  ];

  // const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
  const MAPBOX_TOKEN = 'sk.eyJ1IjoidnZsbGwyZmxyaSIsImEiOiJjbWthajBoM3AyNWJuM2VwdHRqYzh1djZzIn0.uuQQJvrKeBq8zv38EWFXYA';

  // Generate session token for Search Box API
  useEffect(() => {
    setSessionToken(crypto.randomUUID());
  }, []);

  const handleStartDemo = () => {
    setCurrentStep(1);
  };

  const handleNextStep = () => {
    console.log('Next step - location:', selectedLocation || location);
    // Will implement next steps later
    setCurrentStep(currentStep + 1);
  };

  // Function to update dropdown position
  const updateDropdownPosition = () => {
    if (searchBoxRef.current) {
      const rect = searchBoxRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width
      });
    }
  };

  const handleLocationChange = (e) => {
    const value = e.target.value;
    setLocation(value);
    setShowSuggestions(true);
    
    // Update dropdown position
    updateDropdownPosition();
    
    // Clear selected location when user starts typing
    if (value !== selectedLocation?.name) {
      setSelectedLocation(null);
    }
  };

  // Search using Mapbox Search Box API
  const searchPlaces = async (query) => {
    console.log('🔍 Searching for:', query);
    console.log('🗝️ Mapbox token available:', !!MAPBOX_TOKEN);
    console.log('🎫 Session token:', sessionToken);
    
    if (!query.trim() || !MAPBOX_TOKEN || MAPBOX_TOKEN === 'your_mapbox_token_here' || !sessionToken) {
      console.warn('❌ Search cancelled - missing requirements:', {
        query: query.trim(),
        hasToken: !!MAPBOX_TOKEN,
        tokenValue: MAPBOX_TOKEN?.substring(0, 20) + '...',
        hasSessionToken: !!sessionToken
      });
      setSuggestions([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const url = `https://api.mapbox.com/search/searchbox/v1/suggest?` +
        `q=${encodeURIComponent(query)}&` +
        `access_token=${MAPBOX_TOKEN}&` +
        `session_token=${sessionToken}&` +
        `language=en&` +
        `limit=5&` +
        `types=poi,address,place`;
      
      console.log('🌐 API URL:', url.replace(MAPBOX_TOKEN, 'TOKEN_HIDDEN'));
      
      const response = await fetch(url);
      console.log('📡 Response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Search results:', data.suggestions?.length || 0, 'suggestions');
        setSuggestions(data.suggestions || []);
      } else {
        const errorText = await response.text();
        console.error('❌ Mapbox API error:', response.status, response.statusText, errorText);
        setSuggestions([]);
      }
    } catch (error) {
      console.error('💥 Network error:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle search with debouncing
  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      if (location && !selectedLocation) {
        searchPlaces(location);
      } else {
        setSuggestions([]);
      }
    }, 300);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [location, selectedLocation]);

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside both search container and suggestions dropdown
      if (!event.target.closest('.search-container') &&
          !event.target.closest('.suggestions-dropdown')) {
        setShowSuggestions(false);
      }
      // Close trip type dropdown if clicked outside
      if (!event.target.closest('.trip-type-selector')) {
        setShowTripTypeDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle scroll to update dropdown position
  useEffect(() => {
    const handleScroll = () => {
      if (showSuggestions) {
        updateDropdownPosition();
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [showSuggestions]);

  // Handle suggestion selection
  const handleSuggestionSelect = async (suggestion) => {
    console.log('🎯 Selected suggestion:', suggestion);
    console.log('🔄 Setting location to:', suggestion.name);
    
    try {
      // Clear suggestions first
      setSuggestions([]);
      setShowSuggestions(false);
      
      // Update the search box with selected location
      setLocation(suggestion.name);
      
      const locationData = {
        id: suggestion.mapbox_id || Date.now(),
        name: suggestion.name,
        shortName: suggestion.name,
        mapbox_id: suggestion.mapbox_id,
        placeType: suggestion.feature_type || 'poi',
        coordinates: null
      };
      
      setSelectedLocation(locationData);
      console.log('✅ Location state updated');

      // Get weather data for the selected location
      await getWeatherForLocation(suggestion.name);
    } catch (error) {
      console.error('❌ Error in suggestion selection:', error);
    }
  };

  // Get weather data (keeping the original weather functionality)
  const getWeatherForLocation = async (locationName) => {
    setIsLoading(true);
    setWeatherError(false);
    
    const APIKey = '8730dd90f91bc749cd5ce598baa2d2fd';
    
    try {
      const response = await fetch(`https://api.openweathermap.org/data/2.5/weather?q=${locationName}&units=metric&appid=${APIKey}`);
      const json = await response.json();

      if (json.cod === '404') {
        setWeatherError(true);
        setWeatherData(null);
        setTimeout(() => {
          const notFound = document.querySelector('.not-found');
          if (notFound) notFound.classList.add('fadeIn');
        }, 100);
      } else {
        setWeatherError(false);
        setWeatherData(json);
        setTimeout(() => {
          const weatherBox = document.querySelector('.weather-box');
          const weatherDetails = document.querySelector('.weather-details');
          if (weatherBox) weatherBox.classList.add('fadeIn');
          if (weatherDetails) weatherDetails.classList.add('fadeIn');
        }, 100);
      }
    } catch (error) {
      console.error('Weather API error:', error);
      setWeatherError(true);
      setWeatherData(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async () => {
    if (selectedLocation) {
      await getWeatherForLocation(selectedLocation.name);
    } else if (location) {
      await getWeatherForLocation(location);
    }
  };

  // Get place icon based on Mapbox Search Box API suggestion
  const getPlaceIcon = (suggestion) => {
    const featureType = suggestion.feature_type;
    const poiCategory = suggestion.poi_category || [];
    
    // Check POI categories first for more specific icons
    if (poiCategory.includes('restaurant')) return '🍽️';
    if (poiCategory.includes('hotel')) return '🏨';
    if (poiCategory.includes('gas_station')) return '⛽';
    if (poiCategory.includes('shopping')) return '🛍️';
    if (poiCategory.includes('museum')) return '🏛️';
    if (poiCategory.includes('park')) return '🌳';
    if (poiCategory.includes('hospital')) return '🏥';
    if (poiCategory.includes('school')) return '🏫';
    if (poiCategory.includes('bank')) return '🏦';
    if (poiCategory.includes('airport')) return '✈️';
    if (poiCategory.includes('train_station')) return '🚂';
    if (poiCategory.includes('church')) return '⛪';
    if (poiCategory.includes('entertainment')) return '🎭';
    if (poiCategory.includes('tourist_attraction')) return '🎯';
    
    // Fallback to feature type
    switch (featureType) {
      case 'poi':
        return '🏛️';
      case 'place':
        return '🏙️';
      case 'address':
        return '📍';
      case 'street':
        return '🛣️';
      default:
        return '📍';
    }
  };

  const getWeatherIcon = (weatherMain) => {
    // Return placeholder for now - would need actual weather icon images
    switch (weatherMain) {
      case 'Clear':
        return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iNTAiIGN5PSI1MCIgcj0iMzAiIGZpbGw9IiNGRkQ3MDAiLz48L3N2Zz4='; // Sun icon
      case 'Rain':
        return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGVsbGlwc2UgY3g9IjUwIiBjeT0iNDAiIHJ4PSIzMCIgcnk9IjIwIiBmaWxsPSIjNjc4M0E0Ii8+PC9zdmc+'; // Rain icon
      case 'Snow':
        return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGVsbGlwc2UgY3g9IjUwIiBjeT0iNDAiIHJ4PSIzMCIgcnk9IjIwIiBmaWxsPSIjRkZGRkZGIi8+PC9zdmc+'; // Snow icon
      case 'Clouds':
        return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGVsbGlwc2UgY3g9IjUwIiBjeT0iNDAiIHJ4PSIzMCIgcnk9IjIwIiBmaWxsPSIjQzVDNUM1Ii8+PC9zdmc+'; // Cloud icon
      case 'Haze':
      case 'Mist':
        return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGVsbGlwc2UgY3g9IjUwIiBjeT0iNDAiIHJ4PSIzMCIgcnk9IjIwIiBmaWxsPSIjQjBCMEIwIi8+PC9zdmc+'; // Mist icon
      default:
        return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iNTAiIGN5PSI1MCIgcj0iMzAiIGZpbGw9IiNGRkQ3MDAiLz48L3N2Zz4='; // Default icon
    }
  };

  // Calendar helper functions
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month, 1).getDay();
  };

  const isSameDay = (date1, date2) => {
    if (!date1 || !date2) return false;
    return date1.getDate() === date2.getDate() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getFullYear() === date2.getFullYear();
  };

  const handleDayClick = (day) => {
    const clickedDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    setSelectedDate(clickedDate);
  };

  const clearDate = () => {
    setSelectedDate(null);
  };

  // Month navigation functions
  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  // Check if we can go to previous month (only future dates allowed)
  const canGoPrevious = () => {
    const today = new Date();
    const firstDayOfCurrentMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const firstDayOfThisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    return firstDayOfCurrentMonth > firstDayOfThisMonth;
  };

  // Handle trip type selection
  const toggleTripType = (typeId) => {
    setSelectedTripTypes(prev => {
      if (prev.includes(typeId)) {
        return prev.filter(id => id !== typeId);
      } else {
        return [...prev, typeId];
      }
    });
  };

  // Fetch events from API
  const fetchEvents = async () => {
    setIsLoadingEvents(true);
    setApiError(null);

    try {
      // Format date to YYYY-MM-DD
      const formattedDate = selectedDate ? selectedDate.toISOString().split('T')[0] : new Date().toISOString().split('T')[0];

      // Format wake up time to HH:MM
      let formattedWakeUpTime = '08:00'; // default
      if (wakeUpTime) {
        if (wakeUpTime.includes(':')) {
          formattedWakeUpTime = wakeUpTime;
        } else {
          // Map preset values to times
          const wakeUpTimeMap = {
            'early': '06:00',
            'morning': '08:00',
            'late-morning': '10:00',
            'noon': '12:00',
            'flexible': '09:00'
          };
          formattedWakeUpTime = wakeUpTimeMap[wakeUpTime] || '08:00';
        }
      }

      // Format travel preferences
      const travelPreferences = personalPreferences || selectedTripTypes.map(typeId => {
        const type = tripTypes.find(t => t.id === typeId);
        return type?.label.toLowerCase();
      }).join(', ') || 'general sightseeing';

      // Format travel type
      const travelType = selectedTripTypes.length > 0 ? selectedTripTypes[0] : 'leisure';

      const requestBody = {
        destination: selectedLocation?.name || location || 'Paris',
        date: formattedDate,
        wake_up_time: formattedWakeUpTime,
        travel_preferences: travelPreferences,
        budget_per_person: parseFloat(budget) || 100.0,
        number_of_travelers: parseInt(groupSize) || 1,
        travel_type: travelType
      };

      console.log('Sending request:', requestBody);

      const response = await fetch('https://ai-travel-backend-350185136100.us-east1.run.app/api/events', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': 'your-api-key-here'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API error response:', errorText);
        if (response.status === 401) {
          throw new Error('Authentication failed. Please check the API key.');
        } else if (response.status === 500) {
          throw new Error('Server error. Please try again later.');
        } else if (response.status === 404) {
          throw new Error('API endpoint not found. Please check the URL.');
        } else {
          throw new Error(`API error: ${response.status} - ${errorText}`);
        }
      }

      const data = await response.json();
      console.log('Received events:', data);
      setEventsData(data);
      setCurrentStep(6);
    } catch (error) {
      console.error('Error fetching events:', error);
      if (error.message.includes('Failed to fetch') || error.message.includes('CORS')) {
        setApiError('CORS error: The API server needs to allow requests from this origin. Please contact the backend team to add CORS headers.');
      } else {
        setApiError(error.message);
      }
    } finally {
      setIsLoadingEvents(false);
    }
  };

  // Handle Get Inspired button click
  const handleGetInspired = () => {
    fetchEvents();
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);
    const today = new Date();
    const monthYear = currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

    const days = [];
    const prevMonthDays = firstDay;
    const prevMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 0);
    const prevMonthDaysCount = prevMonth.getDate();

    // Previous month days
    for (let i = prevMonthDays - 1; i >= 0; i--) {
      const day = prevMonthDaysCount - i;
      days.push({
        day,
        isPrevMonth: true,
        date: new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, day)
      });
    }

    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), i);
      days.push({
        day: i,
        isPrevMonth: false,
        isNextMonth: false,
        isToday: isSameDay(date, today),
        isSelected: isSameDay(date, selectedDate),
        date
      });
    }

    // Next month days to fill the grid
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        day: i,
        isNextMonth: true,
        date: new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, i)
      });
    }

    const weeks = [];
    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7));
    }

    return (
      <div className="calendar-wrap">
        {/* Month Navigation */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1em'
        }}>
          <button
            className="button"
            onClick={goToPreviousMonth}
            disabled={!canGoPrevious()}
            style={{
              opacity: canGoPrevious() ? 1 : 0.3,
              cursor: canGoPrevious() ? 'pointer' : 'not-allowed',
              padding: '0.5em 1em'
            }}
          >
            ← Prev
          </button>
          <h2 className="month-year" style={{ margin: 0 }}>{monthYear}</h2>
          <button
            className="button"
            onClick={goToNextMonth}
            style={{
              padding: '0.5em 1em'
            }}
          >
            Next →
          </button>
        </div>

        <table className="calendar">
          <thead>
            <tr>
              <th className="day-title" scope="col">Sun</th>
              <th className="day-title" scope="col">Mon</th>
              <th className="day-title" scope="col">Tue</th>
              <th className="day-title" scope="col">Wed</th>
              <th className="day-title" scope="col">Thu</th>
              <th className="day-title" scope="col">Fri</th>
              <th className="day-title" scope="col">Sat</th>
            </tr>
          </thead>
          <tbody>
            {weeks.map((week, weekIndex) => (
              <tr className="week" key={weekIndex}>
                {week.map((dayObj, dayIndex) => {
                  const classNames = ['day'];
                  const isPastDate = dayObj.date < today && !isSameDay(dayObj.date, today);

                  if (dayObj.isPrevMonth) classNames.push('prev-mon');
                  if (dayObj.isNextMonth) classNames.push('next-mon');
                  if (dayObj.isToday) classNames.push('today');
                  if (dayObj.isSelected) classNames.push('selected');
                  if (isPastDate) classNames.push('past-date');

                  return (
                    <td
                      key={dayIndex}
                      className={classNames.join(' ')}
                      tabIndex="0"
                      onClick={() => {
                        if (!dayObj.isPrevMonth && !dayObj.isNextMonth && !isPastDate) {
                          handleDayClick(dayObj.day);
                        }
                      }}
                      style={{
                        opacity: isPastDate ? 0.3 : 1,
                        cursor: isPastDate ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <span className="day-number">{dayObj.day}</span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>

        <a className="button reset" onClick={clearDate}>Clear date</a>
      </div>
    );
  };

  const renderStep = () => {
    switch(currentStep) {
      case 0:
        return (
          <AnimatedButton 
            primaryText="Lets Start"
            secondaryText=" Lets Start"
            onClick={handleStartDemo}
          />
        );
      
      case 1:
        return (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            width: '100%',
            position: 'relative'
          }}>

            {/* Location Search Box */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              gap: 2,
              marginTop: { xs: '10vh', md: weatherData ? '5vh' : '15vh' },
              marginBottom: { xs: '10vh', md: '5vh' },
              width: '100%',
              maxWidth: '90vw',
              paddingBottom: 2
            }}>
              <div className={`search-container ${weatherData ? 'expanded' : ''}`} style={{ position: 'relative' }}>
                <div className="search-box" ref={searchBoxRef}>
                  <i className="fa-solid fa-location-dot"></i>
                  <input 
                    type="text" 
                    placeholder="Enter your location"
                    value={location}
                    onChange={handleLocationChange}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    onFocus={() => {
                      setShowSuggestions(true);
                      // Update dropdown position on focus
                      updateDropdownPosition();
                    }}
                  />
                  <button 
                    className="fa-solid fa-magnifying-glass"
                    onClick={handleSearch}
                    disabled={isLoading}
                  ></button>
                </div>

                
                {/* Debug info */}
                {console.log('🔍 Debug state:', { 
                  showSuggestions, 
                  suggestionsLength: suggestions.length,
                  location,
                  selectedLocation 
                })}

                {/* Mapbox Token Warning */}
                {(!MAPBOX_TOKEN || MAPBOX_TOKEN === 'your_mapbox_token_here') && (
                  <div style={{
                    marginTop: '10px',
                    padding: '8px',
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffeaa7',
                    borderRadius: '6px',
                    fontSize: '12px',
                    color: '#856404'
                  }}>
                    ⚠️ Mapbox token not configured. Using fallback search.
                  </div>
                )}

                <div className="not-found" style={{display: weatherError ? 'block' : 'none'}}>
                  <p>Oops! Invalid location :/</p>
                </div>

                <div className="weather-box" style={{display: weatherData && !weatherError ? 'block' : 'none'}}>
                  <img src={weatherData ? getWeatherIcon(weatherData.weather[0].main) : ''} alt="weather box" />
                  <p className="temperature">
                    {weatherData ? `${parseInt(weatherData.main.temp)}` : ''}
                    {weatherData && <span>°C</span>}
                  </p>
                  <p className="description">{weatherData ? weatherData.weather[0].description : ''}</p>
                </div>

                <div className="weather-details" style={{display: weatherData && !weatherError ? 'flex' : 'none'}}>
                  <div className="humidity">
                    <i className="fa-solid fa-water"></i>
                    <div className="text">
                      <span>{weatherData ? `${weatherData.main.humidity}%` : ''}</span>
                      <p>Humidity</p>
                    </div>
                  </div>
                  <div className="wind">
                    <i className="fa-solid fa-wind"></i>
                    <div className="text">
                      <span>{weatherData ? `${parseInt(weatherData.wind.speed)}Km/h` : ''}</span>
                      <p>Wind Speed</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Next Button */}
              <AnimatedButton 
                primaryText="Next"
                secondaryText="Continue"
                onClick={handleNextStep}
              />
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            gap: 2,
            marginTop: { xs: '2vh', md: '3vh' },
            marginBottom: { xs: '2vh', md: '3vh' }
          }}>
            <Typography variant="h5" sx={{ color: '#fff', fontWeight: 600, textAlign: 'center' }}>
              Select Your Travel Date
            </Typography>

            {renderCalendar()}

            {selectedDate && (
              <Box sx={{
                color: '#fff',
                textAlign: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                padding: 1.5,
                borderRadius: 2,
                backdropFilter: 'blur(10px)'
              }}>
                <Typography variant="body1">
                  Selected: {selectedDate.toLocaleDateString()}
                </Typography>
              </Box>
            )}

            <AnimatedButton
              primaryText="Next"
              secondaryText="Continue"
              onClick={handleNextStep}
            />
          </Box>
        );

      case 3:
        return (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            gap: 3,
            marginTop: { xs: '5vh', md: '10vh' }
          }}>
            <Typography variant="h5" sx={{ color: '#fff', fontWeight: 600, textAlign: 'center' }}>
              Travel Details
            </Typography>

            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2.5,
              width: '100%',
              maxWidth: '400px',
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              padding: 3,
              borderRadius: 3,
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)'
            }}>
              {/* Budget Input */}
              <Box>
                <Typography variant="body1" sx={{
                  color: '#06283D',
                  fontWeight: 600,
                  mb: 1,
                  fontSize: '0.95rem'
                }}>
                  Budget (USD)
                </Typography>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: '#f5f5f5',
                  borderRadius: 2,
                  padding: '4px 16px',
                  border: '2px solid transparent',
                  transition: 'all 0.3s ease',
                  '&:focus-within': {
                    border: '2px solid #03A9F4',
                    backgroundColor: '#fff'
                  }
                }}>
                  <Typography sx={{ color: '#06283D', fontSize: '1.2rem', fontWeight: 500, mr: 1 }}>
                    $
                  </Typography>
                  <input
                    type="number"
                    placeholder="Enter your budget"
                    value={budget}
                    onChange={(e) => setBudget(e.target.value)}
                    style={{
                      border: 'none',
                      outline: 'none',
                      backgroundColor: 'transparent',
                      width: '100%',
                      fontSize: '1rem',
                      color: '#06283D',
                      padding: '12px 0',
                      fontFamily: 'Roboto, sans-serif'
                    }}
                  />
                </Box>
              </Box>

              {/* Group Size Input */}
              <Box>
                <Typography variant="body1" sx={{
                  color: '#06283D',
                  fontWeight: 600,
                  mb: 1,
                  fontSize: '0.95rem'
                }}>
                  Number of People
                </Typography>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: '#f5f5f5',
                  borderRadius: 2,
                  padding: '4px 16px',
                  border: '2px solid transparent',
                  transition: 'all 0.3s ease',
                  '&:focus-within': {
                    border: '2px solid #03A9F4',
                    backgroundColor: '#fff'
                  }
                }}>
                  <i className="fa-solid fa-users" style={{
                    color: '#06283D',
                    fontSize: '1.1rem',
                    marginRight: '12px'
                  }}></i>
                  <input
                    type="number"
                    placeholder="Enter group size"
                    value={groupSize}
                    onChange={(e) => setGroupSize(e.target.value)}
                    min="1"
                    style={{
                      border: 'none',
                      outline: 'none',
                      backgroundColor: 'transparent',
                      width: '100%',
                      fontSize: '1rem',
                      color: '#06283D',
                      padding: '12px 0',
                      fontFamily: 'Roboto, sans-serif'
                    }}
                  />
                </Box>
              </Box>

              {/* Summary */}
              {budget && groupSize && (
                <Box sx={{
                  mt: 1,
                  p: 2,
                  backgroundColor: '#e1f5fe',
                  borderRadius: 2,
                  borderLeft: '4px solid #03A9F4'
                }}>
                  <Typography variant="body2" sx={{ color: '#06283D', fontWeight: 500 }}>
                    Budget per person: ${(parseFloat(budget) / parseInt(groupSize)).toFixed(2)}
                  </Typography>
                </Box>
              )}
            </Box>

            <AnimatedButton
              primaryText="Next"
              secondaryText="Continue"
              onClick={handleNextStep}
            />
          </Box>
        );

      case 4:
        return (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            gap: 3,
            marginTop: { xs: '5vh', md: '10vh' }
          }}>
            <Typography variant="h5" sx={{ color: '#fff', fontWeight: 600, textAlign: 'center' }}>
              Trip Preferences
            </Typography>

            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2.5,
              width: '100%',
              maxWidth: '400px',
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              padding: 3,
              borderRadius: 3,
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)'
            }}>
              {/* Trip Type Multi-Select */}
              <Box className="trip-type-selector">
                <Typography variant="body1" sx={{
                  color: '#06283D',
                  fontWeight: 600,
                  mb: 1,
                  fontSize: '0.95rem'
                }}>
                  Trip Type (Select all that apply)
                </Typography>
                <Box
                  onClick={() => setShowTripTypeDropdown(!showTripTypeDropdown)}
                  sx={{
                    backgroundColor: '#f5f5f5',
                    borderRadius: 2,
                    padding: '12px 16px',
                    border: '2px solid transparent',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      border: '2px solid #03A9F4',
                      backgroundColor: '#fff'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, flex: 1 }}>
                      {selectedTripTypes.length === 0 ? (
                        <Typography sx={{ color: '#666', fontSize: '1rem' }}>
                          Select trip types...
                        </Typography>
                      ) : (
                        selectedTripTypes.map(typeId => {
                          const type = tripTypes.find(t => t.id === typeId);
                          return (
                            <Box key={typeId} sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              backgroundColor: '#03A9F4',
                              color: '#fff',
                              padding: '4px 10px',
                              borderRadius: 1.5,
                              fontSize: '0.85rem'
                            }}>
                              <span>{type?.icon}</span>
                              <span>{type?.label}</span>
                            </Box>
                          );
                        })
                      )}
                    </Box>
                    <i className={`fa-solid fa-chevron-${showTripTypeDropdown ? 'up' : 'down'}`}
                       style={{ color: '#06283D', fontSize: '0.9rem', marginLeft: '8px' }}></i>
                  </Box>
                </Box>

                {/* Dropdown Menu */}
                {showTripTypeDropdown && (
                  <Box sx={{
                    mt: 1,
                    backgroundColor: '#fff',
                    borderRadius: 2,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    maxHeight: '240px',
                    overflowY: 'auto'
                  }}>
                    {tripTypes.map(type => (
                      <Box
                        key={type.id}
                        onClick={() => toggleTripType(type.id)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          padding: '12px 16px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #f0f0f0',
                          transition: 'background-color 0.2s ease',
                          '&:hover': {
                            backgroundColor: '#f5f5f5'
                          },
                          '&:last-child': {
                            borderBottom: 'none'
                          }
                        }}
                      >
                        <Box sx={{
                          width: '20px',
                          height: '20px',
                          border: '2px solid #03A9F4',
                          borderRadius: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: selectedTripTypes.includes(type.id) ? '#03A9F4' : 'transparent'
                        }}>
                          {selectedTripTypes.includes(type.id) && (
                            <i className="fa-solid fa-check" style={{ color: '#fff', fontSize: '0.7rem' }}></i>
                          )}
                        </Box>
                        <span style={{ fontSize: '1.2rem' }}>{type.icon}</span>
                        <Typography sx={{ color: '#06283D', fontSize: '0.95rem', flex: 1 }}>
                          {type.label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>

              {/* Personal Preferences Text Input */}
              <Box>
                <Typography variant="body1" sx={{
                  color: '#06283D',
                  fontWeight: 600,
                  mb: 1,
                  fontSize: '0.95rem'
                }}>
                  Other Preferences (Optional)
                </Typography>
                <Box sx={{
                  backgroundColor: '#f5f5f5',
                  borderRadius: 2,
                  padding: '4px 16px',
                  border: '2px solid transparent',
                  transition: 'all 0.3s ease',
                  '&:focus-within': {
                    border: '2px solid #03A9F4',
                    backgroundColor: '#fff'
                  }
                }}>
                  <textarea
                    placeholder="E.g., dietary restrictions, accessibility needs, interests..."
                    value={personalPreferences}
                    onChange={(e) => setPersonalPreferences(e.target.value)}
                    rows={4}
                    style={{
                      border: 'none',
                      outline: 'none',
                      backgroundColor: 'transparent',
                      width: '100%',
                      fontSize: '1rem',
                      color: '#06283D',
                      padding: '12px 0',
                      fontFamily: 'Roboto, sans-serif',
                      resize: 'vertical'
                    }}
                  />
                </Box>
              </Box>
            </Box>

            <AnimatedButton
              primaryText="Next"
              secondaryText="Continue"
              onClick={handleNextStep}
            />
          </Box>
        );

      case 5:
        return (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            gap: 2,
            marginTop: { xs: '2vh', md: '3vh' },
            marginBottom: { xs: '2vh', md: '3vh' }
          }}>
            <Typography variant="h5" sx={{ color: '#fff', fontWeight: 600, textAlign: 'center' }}>
              Wake Up Time Preference
            </Typography>

            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              width: '100%',
              maxWidth: '380px',
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              padding: 2.5,
              borderRadius: 3,
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)'
            }}>
              <Box>
                <Typography variant="body1" sx={{
                  color: '#06283D',
                  fontWeight: 600,
                  mb: 1.5,
                  fontSize: '0.9rem'
                }}>
                  What time do you prefer to wake up?
                </Typography>

                {/* Time Picker Options */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {[
                    { value: 'early', label: 'Early Bird (5-7 AM)', icon: '🌅' },
                    { value: 'morning', label: 'Morning (7-9 AM)', icon: '☀️' },
                    { value: 'late-morning', label: 'Late Morning (9-11 AM)', icon: '🌤️' },
                    { value: 'noon', label: 'Around Noon (11-1 PM)', icon: '🌞' },
                    { value: 'flexible', label: 'Flexible', icon: '⏰' }
                  ].map((option) => (
                    <Box
                      key={option.value}
                      onClick={() => setWakeUpTime(option.value)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        padding: '10px 12px',
                        backgroundColor: wakeUpTime === option.value ? '#e1f5fe' : '#f5f5f5',
                        border: wakeUpTime === option.value ? '2px solid #03A9F4' : '2px solid transparent',
                        borderRadius: 2,
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          backgroundColor: wakeUpTime === option.value ? '#e1f5fe' : '#e8e8e8',
                          transform: 'translateX(4px)'
                        }
                      }}
                    >
                      <Box sx={{
                        width: '20px',
                        height: '20px',
                        border: '2px solid #03A9F4',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: wakeUpTime === option.value ? '#03A9F4' : 'transparent',
                        flexShrink: 0
                      }}>
                        {wakeUpTime === option.value && (
                          <Box sx={{
                            width: '8px',
                            height: '8px',
                            backgroundColor: '#fff',
                            borderRadius: '50%'
                          }} />
                        )}
                      </Box>
                      <span style={{ fontSize: '1.3rem' }}>{option.icon}</span>
                      <Typography sx={{
                        color: '#06283D',
                        fontSize: '0.88rem',
                        fontWeight: wakeUpTime === option.value ? 600 : 400,
                        flex: 1
                      }}>
                        {option.label}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* Custom Time Input */}
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="body2" sx={{
                    color: '#06283D',
                    fontWeight: 500,
                    mb: 0.8,
                    fontSize: '0.8rem'
                  }}>
                    Or set a specific time:
                  </Typography>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: '#f5f5f5',
                    borderRadius: 2,
                    padding: '4px 12px',
                    border: '2px solid transparent',
                    transition: 'all 0.3s ease',
                    '&:focus-within': {
                      border: '2px solid #03A9F4',
                      backgroundColor: '#fff'
                    }
                  }}>
                    <i className="fa-solid fa-clock" style={{
                      color: '#06283D',
                      fontSize: '0.95rem',
                      marginRight: '10px'
                    }}></i>
                    <input
                      type="time"
                      value={wakeUpTime.includes(':') ? wakeUpTime : ''}
                      onChange={(e) => setWakeUpTime(e.target.value)}
                      style={{
                        border: 'none',
                        outline: 'none',
                        backgroundColor: 'transparent',
                        width: '100%',
                        fontSize: '0.95rem',
                        color: '#06283D',
                        padding: '10px 0',
                        fontFamily: 'Roboto, sans-serif'
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>

            {isLoadingEvents ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={24} sx={{ color: '#fff' }} />
                <Typography sx={{ color: '#fff' }}>Loading events...</Typography>
              </Box>
            ) : (
              <AnimatedButton
                primaryText="Get Inspired"
                secondaryText="Let's Go"
                onClick={handleGetInspired}
              />
            )}

            {apiError && (
              <Box sx={{
                backgroundColor: 'rgba(255, 82, 82, 0.9)',
                color: '#fff',
                padding: 2,
                borderRadius: 2,
                maxWidth: '400px'
              }}>
                <Typography variant="body2">
                  Error: {apiError}
                </Typography>
              </Box>
            )}
          </Box>
        );

      case 6:
        return (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            minHeight: '100vh',
            py: { xs: 2, md: 3 },
            gap: 2
          }}>
            <Typography variant="h4" sx={{
              color: '#fff',
              fontWeight: 600,
              textAlign: 'center',
              fontSize: { xs: '1.75rem', md: '2.125rem' }
            }}>
              Your Personalized Events
            </Typography>

            {eventsData?.eventsSummary && (
              <Box sx={{
                maxWidth: '500px',
                width: '90%',
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                padding: 2,
                borderRadius: 3,
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)'
              }}>
                <Typography variant="body2" sx={{ color: '#06283D', lineHeight: 1.6, fontSize: '14px' }}>
                  {eventsData.eventsSummary}
                </Typography>
              </Box>
            )}

            {eventsData?.availableEvents && eventsData.availableEvents.length > 0 ? (
              <Box sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flex: 1,
                py: 2
              }}>
                <SwipeableCardStack
                  events={eventsData.availableEvents.map(event => ({
                    _id: event.eventName,
                    title: event.eventName,
                    category: event.eventType,
                    description: event.description,
                    location: event.location,
                    start: `${eventsData.date} ${event.startTime}`,
                    end: `${eventsData.date} ${event.endTime}`,
                    duration: event.duration,
                    ticketPrice: event.ticketPrice,
                    bookingInfo: event.bookingInfo,
                    suitability: event.suitability
                  }))}
                  onEventClick={(event) => {
                    console.log('Event clicked:', event);
                  }}
                />
              </Box>
            ) : (
              <Typography variant="body1" sx={{ color: '#fff', mt: 2 }}>
                No events found. Please try different preferences.
              </Typography>
            )}

            <AnimatedButton
              primaryText="Start Over"
              secondaryText="New Search"
              onClick={() => {
                setCurrentStep(0);
                setEventsData(null);
                setApiError(null);
              }}
            />
          </Box>
        );

      default:
        return (
          <Typography variant="h4" sx={{ color: '#fff' }}>
            Step {currentStep} - Coming Soon!
          </Typography>
        );
    }
  };

  return (
    <>
      {/* Font Awesome for icons */}
      <script src="https://kit.fontawesome.com/7c8801c017.js" crossorigin="anonymous"></script>
      
      {/* Search Box Styles */}
      <style>
        {`
          @import url('https://fonts.googleapis.com/css?family=Poppins:300,400,500,600,700,800,900|Roboto:300,400,500,700,900&display=swap');
          
          .search-container {
            position: relative;
            width: 400px;
            height: 105px;
            background: #fff;
            padding: 28px 32px;
            overflow: visible;
            border-radius: 18px;
            font-family: 'Roboto', sans-serif;
            transition: 0.6s ease-out;
          }
          
          .search-container.expanded {
            height: 500px;
            overflow: visible;
          }
          
          .search-box {
            width: 100%;
            height: min-content;
            display: flex;
            align-items: center;
            justify-content: space-between;
          }
          
          .search-box input {
            color: #06283d;
            width: 80%;
            font-size: 24px;
            font-weight: 500;
            text-transform: none;
            padding-left: 32px;
            background: none;
            border: none;
            outline: none;
          }
          
          .search-box input::placeholder {
            font-size: 20px;
            font-weight: 500;
            color: #06283D;
            text-transform: capitalize;
          }
          
          .search-box button {
            cursor: pointer;
            width: 50px;
            height: 50px;
            color: #06283D;
            background: #dff6ff;
            border-radius: 50%;
            font-size: 22px;
            border: none;
            transition: 0.4s ease;
          }
          
          .search-box button:hover {
            color: #fff;
            background: #06283D;
          }
          
          .search-box i {
            position: absolute;
            color: #06283D;
            font-size: 28px;
          }
          
          .weather-box {
            text-align: center;
            scale: 0;
            opacity: 0;
          }
          
          .weather-box img {
            width: 40%;
            margin-top: 20px;
          }
          
          .weather-box .temperature {
            position: relative;
            color: #06283D;
            font-size: 2.5rem;
            font-weight: 800;
            margin-top: 20px;
            margin-left: -10px;
          }
          
          .weather-box .temperature span {
            position: absolute;
            margin-left: 4px;
            font-size: 1rem;
          }
          
          .weather-box .description {
            color: #06283D;
            font-size: 16px;
            font-weight: 500;
            text-transform: capitalize;
            margin: 10px 0;
          }
          
          .weather-details {
            width: 100%;
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-top: 20px;
            padding: 0 10px;
            scale: 0;
            opacity: 0;
          }
          
          .weather-details .humidity {
            display: flex;
            align-items: center;
            width: 50%;
            height: 80px;
            justify-content: flex-start;
            padding-left: 10px;
          }
          
          .weather-details .wind {
            display: flex;
            align-items: center;
            width: 50%;
            height: 80px;
            justify-content: flex-end;
            padding-right: 10px;
          }
          
          .weather-details .text {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            text-align: left;
            margin-left: 8px;
          }
          
          .weather-details .wind .text {
            align-items: flex-end;
            text-align: right;
            margin-left: 0;
            margin-right: 8px;
          }
          
          .weather-details i {
            color: #06283D;
            font-size: 20px;
            margin-right: 8px;
            margin-top: 4px;
          }
          
          .weather-details span {
            color: #06283D;
            font-size: 16px;
            font-weight: 500;
          }
          
          .weather-details p {
            color: #06283D;
            font-size: 12px;
            font-weight: 500;
            margin: 0;
          }
          
          .not-found {
            width: 100%;
            text-align: center;
            margin-top: 50px;
            scale: 0;
            opacity: 0;
            display: none;
          }
          
          .not-found p {
            color: #06283D;
            font-size: 22px;
            font-weight: 500;
            margin-top: 12px;
          }
          
          .fadeIn {
            animation: 0.5s fadeIn forwards;
            animation-delay: 0.5s;
          }
          
          @keyframes fadeIn {
            to {
              scale: 1;
              opacity: 1;
            }
          }
          
          @media (max-width: 768px) {
            .search-container {
              width: 95%;
              max-width: 350px;
            }
            
            .search-container.expanded {
              max-height: 80vh;
              overflow-y: auto;
            }
            
            .search-box input {
              font-size: 18px;
            }
            
            .search-box input::placeholder {
              font-size: 16px;
            }
            
            .weather-box .temperature {
              font-size: 2rem;
            }
            
            .weather-box .description {
              font-size: 14px;
            }
            
            .weather-details span {
              font-size: 14px;
            }
            
            .weather-details i {
              font-size: 16px;
            }
          }
          
          /* Mapbox Suggestions Dropdown */
          .suggestions-dropdown {
            position: fixed;
            background: white;
            border-radius: 12px;
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
            border: 1px solid rgba(6, 40, 61, 0.1);
            max-height: 200px;
            overflow-y: auto;
            z-index: 99999;
            margin-top: 4px;
            display: block !important;
            visibility: visible !important;
            opacity: 1 !important;
            width: 336px;
            min-height: 100px;
          }
          
          .suggestion-item {
            padding: 10px 16px;
            cursor: pointer;
            border-bottom: 1px solid rgba(0, 0, 0, 0.05);
            display: flex !important;
            align-items: center;
            gap: 12px;
            transition: background-color 0.2s ease;
            background-color: white;
            min-height: 50px;
          }
          
          .suggestion-item:hover {
            background-color: rgba(6, 40, 61, 0.1) !important;
          }
          
          .suggestion-item:last-child {
            border-bottom: none;
          }
          
          .suggestion-icon {
            font-size: 18px;
            width: 20px;
            text-align: center;
            flex-shrink: 0;
          }
          
          .suggestion-text {
            flex: 1;
            min-width: 0;
          }
          
          .suggestion-name {
            font-weight: 600;
            color: #06283d;
            font-size: 14px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          
          .suggestion-address {
            color: #666;
            font-size: 12px;
            margin-top: 2px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          /* Calendar Styles */
          .calendar-wrap {
            width: 100%;
            max-width: 350px;
            padding: 0.6em;
            margin: auto;
            font-size: 3.5vw;
            background-color: #fff;
            border-radius: 1.2em;
            user-select: none;
            box-shadow:
              0 0.0625em 0.25em rgba(0,20,50,0.2),
              0 0.5em 0.5em 0.125em rgba(0,20,50,0.1);
          }

          @media (min-width: 500px) {
            .calendar-wrap {
              width: 350px;
              font-size: 1rem;
            }
          }

          .month-year {
            margin-bottom: 0.5em;
            font-weight: normal;
            font-size: 1.1em;
            text-align: center;
          }

          .calendar {
            table-layout: fixed;
            width: 100%;
            margin-bottom: 0.5em;
            overflow: hidden;
            border-collapse: collapse;
            -webkit-tap-highlight-color: transparent;
          }

          .day-title {
            width: 3em;
            height: 3em;
            vertical-align: top;
            font-weight: bold;
            font-size: 0.7em;
            text-transform: uppercase;
            letter-spacing: 0.0625em;
            box-shadow:
              inset 0 -0.875em #fff,
              inset 0 -1em rgba(0,20,50,0.1);
          }

          .day {
            position: relative;
            height: 2.5em;
            cursor: pointer;
          }

          .day:focus {
            outline: none;
          }

          .day:focus .day-number::after {
            content: '';
            position: absolute;
            z-index: -1;
            top: 50%;
            left: 50%;
            width: 80%;
            height: 80%;
            transform: translate(-50%,-50%);
            border: 0.25em solid #0288d1;
            border-radius: 50%;
          }

          .day.active-a:focus .day-number::after,
          .day.active-b:focus .day-number::after {
            width: 100%;
            height: 100%;
          }

          .day.today:focus .day-number::after {
            width: 102%;
            height: 102%;
          }

          .day:hover span.day-number {
            color: #0288d1;
          }

          .day-number {
            position: relative;
            z-index: 1;
            display: flex;
            justify-content: center;
            align-items: center;
            height: 2.5em;
            border-radius: 50%;
          }

          .day.selected span.day-number {
            color: #fff;
            background-color: #03A9F4;
          }

          .day.prev-mon .day-number,
          .day.next-mon .day-number {
            color: rgba(0,0,0,0.3);
          }

          .day.today span.day-number {
            color: #fff;
            border: 0.2em solid transparent;
            box-shadow: inset 0 0 0 2em #37474F;
          }

          .calendar-wrap .reset {
            display: block;
            color: #fff;
            text-align: center;
          }

          .button {
            display: inline-block;
            padding: 0.4em 0.9em;
            color: #fff;
            font-weight: bold;
            font-size: 0.65em;
            text-align: center;
            text-transform: uppercase;
            letter-spacing: 0.0625em;
            background-color: #37474F;
            border-radius: 1.5em;
            cursor: pointer;
            text-decoration: none;
          }

          .button:hover {
            background-color: #0288d1;
          }
        `}
      </style>

      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'linear-gradient(45deg, #FF6B6B, #4ECDC4, #45B7D1, #96CEB4, #FFEAA7, #DDA0DD)',
          backgroundSize: '400% 400%',
          animation: `${backgroundAnimation} 4s ease infinite`,
          overflowY: 'auto',
          overflowX: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}
      >
        {renderStep()}
      </Box>
      
      {/* Mapbox Suggestions Dropdown - Rendered at document level */}
      {showSuggestions && suggestions.length > 0 && (
        <div 
          className="suggestions-dropdown"
          style={{
            position: 'fixed',
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
            backgroundColor: 'white',
            border: '1px solid rgba(6, 40, 61, 0.1)',
            borderRadius: '18px',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
            zIndex: 999999,
            maxHeight: '240px',
            overflowY: 'auto'
          }}
        >
          {console.log('🎯 Rendering dropdown at position:', dropdownPosition, 'with', suggestions.length, 'suggestions') || 
           suggestions.map((suggestion, index) => (
            <div
              key={suggestion.mapbox_id || index}
              className="suggestion-item"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('🖱️ Suggestion clicked:', suggestion.name);
                handleSuggestionSelect(suggestion);
              }}
              style={{
                padding: '12px 16px',
                cursor: 'pointer',
                borderBottom: index < suggestions.length - 1 ? '1px solid rgba(0, 0, 0, 0.1)' : 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                backgroundColor: index % 2 === 0 ? '#f9f9f9' : 'white'
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(6, 40, 61, 0.1)'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#f9f9f9' : 'white'}
            >
              <span style={{ fontSize: '18px', width: '20px', textAlign: 'center' }}>
                {getPlaceIcon(suggestion)}
              </span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, color: '#06283d', fontSize: '14px' }}>
                  {suggestion.name}
                </div>
                <div style={{ color: '#666', fontSize: '12px', marginTop: '2px' }}>
                  {suggestion.full_address || suggestion.place_formatted || 'Point of Interest'}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default Demo;